import { useRef, useState, useEffect } from "react";
import { Play, Pause, X, SpeakerHigh, SpeakerSlash, Headphones, SlidersHorizontal, DownloadSimple, ArrowsClockwise, GearSix, Repeat } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { audioSrc, apiClient, API } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

let _audioCtx = null;
function getAudioCtx() {
  if (!_audioCtx) {
    try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch { /* noop */ }
  }
  return _audioCtx;
}

function Track({ track, onUpdate, onRemove, masterPlaying }) {
  const audioRef = useRef(null);
  const pannerRef = useRef(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const ctx = getAudioCtx();
    if (!ctx) return;
    try {
      if (!a._panner) {
        const src = ctx.createMediaElementSource(a);
        const panner = ctx.createStereoPanner();
        src.connect(panner);
        panner.connect(ctx.destination);
        a._panner = panner;
      }
      pannerRef.current = a._panner;
    } catch { /* pan preview unavailable */ }
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = track.muted ? 0 : track.volume;
    a.loop = !!track.loop;
    if (pannerRef.current) pannerRef.current.pan.value = (track.pan ?? 0) / 100;
  }, [track.volume, track.muted, track.loop, track.pan]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    let timer = null;
    if (masterPlaying) {
      getAudioCtx()?.resume?.();
      a.currentTime = 0;
      const d = (track.delay ?? 0) * 1000;
      if (d > 0) timer = setTimeout(() => a.play().catch(() => {}), d);
      else a.play().catch(() => {});
    } else {
      a.pause();
    }
    return () => timer && clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterPlaying]);

  const setting = (label, value, unit, onChange, min, max, step, testid) => (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-mono-num uppercase tracking-widest text-white/40">{label}</span>
        <span className="text-[10px] font-mono-num text-white/60">{value}{unit}</span>
      </div>
      <Slider data-testid={testid} value={[value]} onValueChange={(v) => onChange(v[0])} min={min} max={max} step={step} />
    </div>
  );

  return (
    <div
      data-testid="mixer-track"
      className={`relative rounded-xl border bg-[color:var(--surface-2)] px-4 py-3 ${
        masterPlaying && !track.muted ? "tracing-beam border-transparent" : "border-white/10"
      }`}
    >
      <audio ref={audioRef} src={audioSrc(track.sound)} preload="auto" crossOrigin="use-credentials" />
      <div className="grid grid-cols-12 gap-4 items-center">
        <div className="col-span-12 md:col-span-4 min-w-0">
          <p className="text-sm text-white/90 truncate font-medium">{track.sound.prompt}</p>
          <p className="text-[10px] font-mono-num uppercase tracking-widest text-white/40 mt-0.5">
            {track.sound.category || "uncat."} · {track.sound.duration_seconds ? `${Number(track.sound.duration_seconds).toFixed(1)}s` : "auto"}
            {(track.delay ?? 0) > 0 && <span className="text-[color:var(--accent)]"> · +{track.delay}s</span>}
            {track.loop && <span className="text-[color:var(--accent)]"> · loop</span>}
          </p>
        </div>
        <div className="col-span-8 md:col-span-5 flex items-center gap-3">
          {track.muted ? (
            <SpeakerSlash size={16} className="text-white/40 shrink-0" />
          ) : (
            <SpeakerHigh size={16} className="text-white/60 shrink-0" />
          )}
          <Slider
            data-testid="track-volume-slider"
            value={[Math.round(track.volume * 100)]}
            onValueChange={(v) => onUpdate({ volume: v[0] / 100 })}
            min={0}
            max={100}
            step={1}
            className="flex-1"
          />
          <span className="font-mono-num text-xs text-white/60 w-9 text-right">{Math.round(track.volume * 100)}</span>
        </div>
        <div className="col-span-4 md:col-span-3 flex items-center justify-end gap-1">
          <button
            data-testid="loop-track-btn"
            onClick={() => onUpdate({ loop: !track.loop })}
            title={t("loopLbl")}
            className={`p-1.5 rounded-md transition-colors ${
              track.loop ? "bg-[color:var(--accent)] text-white" : "bg-white/5 text-white/50 hover:text-white"
            }`}
          >
            <Repeat size={14} weight="bold" />
          </button>
          <button
            data-testid="track-settings-btn"
            onClick={() => setSettingsOpen((o) => !o)}
            title={t("trackSettings")}
            className={`p-1.5 rounded-md transition-colors ${
              settingsOpen ? "bg-white/15 text-white" : "bg-white/5 text-white/50 hover:text-white"
            }`}
          >
            <GearSix size={14} weight="bold" />
          </button>
          <button
            data-testid="mute-track-btn"
            onClick={() => onUpdate({ muted: !track.muted })}
            title="Mute"
            className={`px-2 py-1 rounded-md text-[10px] font-mono-num uppercase tracking-widest transition-colors ${
              track.muted ? "bg-[color:var(--accent)] text-white" : "bg-white/5 text-white/60 hover:text-white"
            }`}
          >
            M
          </button>
          <button
            data-testid="remove-track-btn"
            onClick={onRemove}
            title="Remove"
            className="p-1.5 rounded-md hover:bg-white/5 text-white/50 hover:text-white"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      </div>
      {settingsOpen && (
        <div data-testid="track-settings-panel" className="mt-3 pt-3 border-t border-white/5 grid grid-cols-2 md:grid-cols-4 gap-4">
          {setting(t("delayLbl"), track.delay ?? 0, "s", (v) => onUpdate({ delay: v }), 0, 20, 0.5, "track-delay-slider")}
          {setting(t("fadeInLbl"), track.fadeIn ?? 0, "s", (v) => onUpdate({ fadeIn: v }), 0, 5, 0.5, "track-fadein-slider")}
          {setting(t("fadeOutLbl"), track.fadeOut ?? 0, "s", (v) => onUpdate({ fadeOut: v }), 0, 5, 0.5, "track-fadeout-slider")}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-mono-num uppercase tracking-widest text-white/40">{t("panLbl")}</span>
              <span className="text-[10px] font-mono-num text-white/60">
                {(track.pan ?? 0) === 0 ? "C" : (track.pan ?? 0) < 0 ? `L${Math.abs(track.pan)}` : `R${track.pan}`}
              </span>
            </div>
            <Slider data-testid="track-pan-slider" value={[track.pan ?? 0]} onValueChange={(v) => onUpdate({ pan: v[0] })} min={-100} max={100} step={5} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function Mixer({ tracks, onUpdateTrack, onRemoveTrack, onExported }) {
  const [playing, setPlaying] = useState(false);
  const { t } = useLang();
  const [master, setMaster] = useState([85]);
  const [exportOpen, setExportOpen] = useState(false);
  const [mixName, setMixName] = useState("");
  const [mixTag, setMixTag] = useState("");
  const [categories, setCategories] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (tracks.length === 0) setPlaying(false);
  }, [tracks.length]);

  useEffect(() => {
    if (exportOpen && categories.length === 0) {
      apiClient.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    }
  }, [exportOpen, categories.length]);

  const exportMix = async () => {
    if (!mixName.trim() || !mixTag) return;
    setExporting(true);
    try {
      const volumes = {};
      const settings = {};
      tracks.forEach((tr) => {
        volumes[tr.sound.sound_id] = tr.muted ? 0 : Math.round(tr.volume * 100);
        settings[tr.sound.sound_id] = {
          delay: tr.delay ?? 0,
          fade_in: tr.fadeIn ?? 0,
          fade_out: tr.fadeOut ?? 0,
          pan: tr.pan ?? 0,
          loop: !!tr.loop,
        };
      });
      const { data } = await apiClient.post("/mixer/export", {
        sound_ids: tracks.map((tr) => tr.sound.sound_id),
        volumes,
        settings,
        name: mixName.trim(),
        tag: mixTag,
      });
      onExported?.(data);
      toast.success(t("mixSaved"));
      const link = document.createElement("a");
      link.href = `${API}/sounds/${data.sound_id}/download`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setExportOpen(false);
      setMixName("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <section
      data-testid="mixer-panel"
      className="rounded-2xl border border-white/10 bg-[color:var(--surface)] overflow-hidden"
    >
      <div className="px-6 py-5 border-b border-white/5 flex items-center justify-between">
        <div>
          <p className="text-xs font-mono-num uppercase tracking-widest text-white/40">{t("controlRoom")}</p>
          <h2 className="font-display font-semibold text-xl tracking-tight mt-0.5 inline-flex items-center gap-2">
            <SlidersHorizontal size={20} /> {t("mixerTitle")}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono-num text-white/40 uppercase tracking-widest">
            {tracks.length} / 6
          </span>
          <button
            data-testid="export-mix-btn"
            onClick={() => setExportOpen((o) => !o)}
            disabled={tracks.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/30 transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <DownloadSimple size={15} weight="bold" /> {t("exportMix")}
          </button>
          <button
            data-testid="mixer-play-btn"
            onClick={() => setPlaying((p) => !p)}
            disabled={tracks.length === 0}
            className="btn-primary px-5 py-2.5 rounded-full inline-flex items-center gap-2 text-sm font-medium"
          >
            {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
            {playing ? t("stop") : t("playAll")}
          </button>
        </div>
      </div>

      {exportOpen && tracks.length > 0 && (
        <div data-testid="export-mix-panel" className="px-6 py-4 border-b border-white/5 bg-[color:var(--surface-2)]/40 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className="text-[10px] font-mono-num uppercase tracking-widest text-white/40 block mb-1.5">{t("mixName")}</label>
            <Input
              data-testid="mix-name-input"
              value={mixName}
              onChange={(e) => setMixName(e.target.value)}
              maxLength={60}
              placeholder="ghost-ship"
              className="bg-[color:var(--surface-2)] border-white/10 text-white h-10"
            />
          </div>
          <div className="w-[160px]">
            <label className="text-[10px] font-mono-num uppercase tracking-widest text-white/40 block mb-1.5">{t("mainTag")}</label>
            <Select value={mixTag} onValueChange={setMixTag}>
              <SelectTrigger data-testid="mix-tag-select" className="bg-[color:var(--surface-2)] border-white/10 text-white">
                <SelectValue placeholder={t("none")} />
              </SelectTrigger>
              <SelectContent className="bg-[color:var(--surface-2)] border-white/10 text-white">
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id} className="focus:bg-white/10">{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <button
            data-testid="confirm-export-btn"
            onClick={exportMix}
            disabled={exporting || !mixName.trim() || !mixTag}
            className="btn-primary px-5 h-10 rounded-xl text-sm font-medium inline-flex items-center gap-2 disabled:opacity-40"
          >
            {exporting ? (
              <><ArrowsClockwise size={15} className="animate-spin" /> {t("exporting")}</>
            ) : (
              <><DownloadSimple size={15} weight="bold" /> {t("exportBtn")} MP3</>
            )}
          </button>
          {mixName.trim() && mixTag && (
            <p className="w-full text-[10px] font-mono-num text-white/30 tracking-wider">
              → {mixTag.toUpperCase()}_{(mixName.trim().toLowerCase().match(/[a-z0-9]+/g) || ["mix"]).slice(0, 4).join("-")}_mix.mp3
            </p>
          )}
        </div>
      )}

      <div className="p-4 space-y-2">
        {tracks.length === 0 ? (
          <div className="text-center py-12">
            <Headphones size={36} weight="duotone" className="mx-auto text-white/20" />
            <p className="mt-3 text-white/40 text-sm">{t("mixerEmpty")}</p>
          </div>
        ) : (
          tracks.map((trk) => (
            <Track
              key={trk.sound.sound_id}
              track={trk}
              masterPlaying={playing}
              onUpdate={(patch) => onUpdateTrack(trk.sound.sound_id, patch)}
              onRemove={() => onRemoveTrack(trk.sound.sound_id)}
            />
          ))
        )}
      </div>

      {tracks.length > 0 && (
        <div className="px-6 py-4 border-t border-white/5 flex items-center gap-4 bg-[color:var(--surface-2)]/40">
          <span className="text-[10px] font-mono-num uppercase tracking-widest text-white/40">{t("master")}</span>
          <Slider
            data-testid="master-volume-slider"
            value={master}
            onValueChange={setMaster}
            min={0}
            max={100}
            step={1}
            className="flex-1 max-w-[300px]"
          />
          <span className="font-mono-num text-xs text-white/70 w-9">{master[0]}</span>
        </div>
      )}
    </section>
  );
}
